/**
 * Reactify Sidebar (with company logo & name)
 * - Fetches company details on mount if missing (prevents "No logo" after refresh)
 * - Robust absolute URL construction + cache-busting
 * - Clean fallback avatar on error
 */
import React, { Component, Fragment } from 'react';
import classNames from 'classnames';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { Scrollbars } from 'react-custom-scrollbars';

// Material-UI components
import { Button, Avatar, Typography } from '@material-ui/core';
import { TrendingUp } from '@material-ui/icons';

// redux actions
import { collapsedSidebarAction } from 'Store/Actions';
import { fetchCompanyDetails } from 'Store/Actions/companyActions';
import { getAbsoluteApiAssetUrl } from 'Api/apiHelpers';

// components
import UserBlock from './UserBlock';
import SidebarContent from './SidebarContent';
import AgencySidebar from '../AgencyMenu/AgencySidebar';


class Sidebar extends Component {
  constructor(props) {
    super(props);
    this.state = {
      windowWidth: typeof window !== 'undefined' ? window.innerWidth : 1200,
      windowHeight: typeof window !== 'undefined' ? window.innerHeight : 800,
      logoLoadFailed: false
    };
  }

  UNSAFE_componentWillMount() {
    this.updateDimensions();
  }

  componentDidMount() {
    window.addEventListener("resize", this.updateDimensions);

    // 🛠 FIX: fetch company details if not already present (so logo is available after refresh)
    if (!this.props.company) {
      // fetchCompanyDetails will populate Redux company state used by Sidebar
      try {
        this.props.fetchCompanyDetails();
      } catch (err) {
        // swallow — fetchCompanyDetails handles notifications
        console.warn('fetchCompanyDetails threw', err);
      }
    }
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.updateDimensions);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { windowWidth } = this.state;
    if (nextProps.location !== this.props.location) {
      if (windowWidth <= 1199) {
        this.props.collapsedSidebarAction(false);
      }
    }
  }

  updateDimensions = () => {
    this.setState({ windowWidth: window.innerWidth, windowHeight: window.innerHeight });
  }

  // LogoUrl is stored as a root-relative static path (e.g. /uploads/logos/...).
  // Resolve it against the API host, stripping the /api suffix from REACT_APP_BASE_URL.
  buildLogoUrl(rawLogo) {
    return getAbsoluteApiAssetUrl(rawLogo);
  }

  renderLogoBlock = () => {
    const { company } = this.props;
    const { logoLoadFailed } = this.state;

    const rawLogo = company && (company.logoUrl || company.LogoUrl) ? (company.logoUrl || company.LogoUrl) : null;
    const companyName = company && (company.companyName || company.CompanyName) ? (company.companyName || company.CompanyName) : 'HR Analytix';

    const logoAbsoluteUrl = rawLogo ? this.buildLogoUrl(rawLogo) : null;

    // cache-buster: prefer updatedAt, UpdatedAt, or CreatedAt if available
    const stamp = company?.updatedAt ?? company?.UpdatedAt ?? company?.createdAt ?? company?.CreatedAt ?? null;
    const cacheSuffix = stamp ? `?v=${new Date(stamp).getTime()}` : '';

    // initials fallback
    const initials = companyName.split(/\s+/).slice(0,2).map(s => s[0]?.toUpperCase() || '').join('');

    return (
      <div
        className="site-logo hr-main-sidebar-brand"
        style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', padding: '10px 12px' }}
        onClick={() => this.props.history.push('/app/dashboard/company-details')}
        title={companyName}
      >
        {logoAbsoluteUrl && !logoLoadFailed ? (
          <img
            src={logoAbsoluteUrl + cacheSuffix}
            className="mr-15 ml-15"
            alt={`${companyName} logo`}
            width="50"
            height="50"
            style={{ objectFit: 'cover', borderRadius: 8, border: '1px solid rgba(0,0,0,0.06)' }}
            onError={(e) => {
              // mark failed so we don't keep trying to show broken image
              this.setState({ logoLoadFailed: true });
              e.currentTarget.style.display = 'none';
            }}
          />
        ) : (
          <Avatar
            className="fallback-avatar"
            style={{ width: 42, height: 42, background: 'rgba(45,96,255,.10)', color: '#2d60ff', fontWeight: 800 }}
          >
            {initials || 'HA'}
          </Avatar>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1 }}>
          <Typography variant="subtitle1" style={{ fontWeight: 800, color: '#343c6a' }}>
            {companyName}
          </Typography>
          <Typography variant="caption" style={{ color: '#718ebf' }}>
            {company && company.isSubcompany ? 'Sub Company' : 'Company'}
          </Typography>
        </div>
      </div>
    );
  }

  render() {
    const { enableSidebarBackgroundImage, selectedSidebarImage, isDarkSidenav, agencySidebar } = this.props;
    const isSuper = localStorage.getItem('access') === 'superUser' || localStorage.getItem('access') === 'superAdmin';

    return (
      <Fragment>
        <div
          className={classNames('rct-sidebar hr-main-sidebar', { 'background-none': !enableSidebarBackgroundImage })}
          style={{ backgroundImage: enableSidebarBackgroundImage ? `url(${selectedSidebarImage})` : 'none' }}
        >
          <div 
            className={classNames("rct-sidebar-content", { "sidebar-overlay-dark": isDarkSidenav, 'sidebar-overlay-light': !isDarkSidenav })}
            style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}
          >
            {this.renderLogoBlock()}

            <div className="rct-sidebar-wrap" style={{ flex: 1, minHeight: 0, position: 'relative' }}>
              <Scrollbars
                className="rct-scroll"
                autoHide
                autoHideDuration={100}
                style={{ height: '100%' }}
              >
                <UserBlock />
                {!agencySidebar ? <SidebarContent /> : <AgencySidebar />}
              </Scrollbars>
            </div>

            {isSuper && (
              <div 
                className="sidebar-footer border-top" 
                style={{ padding: '12px 16px', borderTop: '1px solid #e6eff5', flexShrink: 0, background: 'inherit' }}
              >
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  onClick={() => this.props.history.push('/app/dashboard/plan-billing')}
                  className="upgrade-plan-btn"
                  style={{
                    background: 'linear-gradient(135deg, #1814f3 0%, #2d60ff 100%)',
                    color: 'white',
                    borderRadius: '10px',
                    padding: '10px 14px',
                    fontWeight: 700,
                    textTransform: 'none',
                    fontSize: '0.9rem',
                    boxShadow: '0 4px 12px rgba(45, 96, 255, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <TrendingUp style={{ fontSize: '1.2rem', marginRight: '8px' }} />
                  Manage Plan
                </Button>
              </div>
            )}
          </div>
        </div>
      </Fragment>
    );
  }
}

// map state to props
const mapStateToProps = ({ settings, company }) => {
  const { enableSidebarBackgroundImage, selectedSidebarImage, collapsedSidebar, isDarkSidenav, locale } = settings;
  return {
    enableSidebarBackgroundImage,
    selectedSidebarImage,
    collapsedSidebar,
    isDarkSidenav,
    locale,
    company: (company && company.company) ? company.company : null
  };
};

export default withRouter(connect(mapStateToProps, {
  collapsedSidebarAction,
  fetchCompanyDetails
})(Sidebar));